import { type ReadonlySignal, signal } from '@preact/signals-core';
import type {
  DatabaseShardManifest,
  DatabaseShardManifestEntry,
} from '@streamlain/database-identity';

/** Shards pulled at the same time; bounds network and storage pressure. */
export const DATABASE_OFFLINE_COPY_CONCURRENCY = 4;
/** Progress is persisted at most this often, and always when a run ends. */
const RECORD_FLUSH_MS = 1000;
/** Root updates arrive in bursts; one manifest pass per burst. */
const MANIFEST_SETTLE_MS = 250;

/** Device-local state of the "Keep available offline" setting. */
export type DatabaseOfflineCopyRecord = {
  enabled: boolean;
  startedAt: number;
  /** Set once every shard was local; later runs only follow changes. */
  completedAt?: number;
  /** shardId -> fingerprint of the manifest entry whose content is local. */
  pulled: Record<string, string>;
};

/** Host half: device-local storage and the existing nbstore sync pull. */
export type DatabaseOfflineCopyPort = {
  read(databaseId: string): DatabaseOfflineCopyRecord | undefined;
  write(databaseId: string, record: DatabaseOfflineCopyRecord): void;
  /** Pulls the server's missing updates of one row shard into local storage
   * without loading it into memory. */
  pull(docId: string, signal: AbortSignal): Promise<void>;
  /** One start and one end (or failure) message per download. */
  notify(event: DatabaseOfflineCopyEvent): void;
};

export type DatabaseOfflineCopyEvent =
  | { kind: 'started'; bytes: number }
  | { kind: 'completed' }
  | { kind: 'failed'; reason: string };

export type DatabaseOfflineCopyProgress = {
  enabled: boolean;
  status: 'idle' | 'running' | 'complete' | 'failed';
  /** The first complete download, as opposed to following later changes. */
  initial: boolean;
  loadedRows: number;
  totalRows: number;
  totalBytes: number;
  error?: string;
};

export type DatabaseOfflineCopySource = {
  databaseId: string;
  manifest(): DatabaseShardManifest | undefined;
  /** Calls back on every update of the database root document. */
  subscribe(onChange: () => void): () => void;
};

function fingerprint(shard: DatabaseShardManifestEntry) {
  return [
    shard.serverDocId,
    shard.rowCount,
    shard.encodedBytes,
    shard.lastRowId ?? '',
  ].join(':');
}

function errorReason(error: unknown) {
  return error instanceof Error ? error.message : String(error);
}

/**
 * Keeps every row shard of one database in local storage while its setting
 * is on: one background download, afterwards only new or changed shards.
 * Shards already local are never removed.
 */
export class DatabaseOfflineCopy {
  private readonly _progress$ = signal<DatabaseOfflineCopyProgress>({
    enabled: false,
    status: 'idle',
    initial: false,
    loadedRows: 0,
    totalRows: 0,
    totalBytes: 0,
  });

  readonly progress$: ReadonlySignal<DatabaseOfflineCopyProgress> =
    this._progress$;

  private record: DatabaseOfflineCopyRecord | undefined;
  private run: AbortController | null = null;
  private rerun = false;
  private flushTimer: ReturnType<typeof setTimeout> | null = null;
  private settleTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly unsubscribe: () => void;
  private disposed = false;
  /** A download whose end the user still expects to hear about. */
  private announced = false;

  constructor(
    private readonly port: DatabaseOfflineCopyPort,
    private readonly source: DatabaseOfflineCopySource
  ) {
    this.record = port.read(source.databaseId);
    this.unsubscribe = source.subscribe(() => this.manifestChanged());
    this.publish();
    if (this.record?.enabled) {
      // A download resumed after a reload still reports its end.
      this.announced = this.record.completedAt === undefined;
      this.start();
    }
  }

  get enabled() {
    return !!this.record?.enabled;
  }

  setEnabled(enabled: boolean) {
    if (this.disposed || enabled === this.enabled) return;
    if (!enabled) {
      this.stop();
      this.record = { ...this.record!, enabled: false };
      this.flush();
      this.publish();
      return;
    }
    // Re-enabling keeps the fingerprints: shards unchanged since are local.
    this.record = {
      pulled: this.record?.pulled ?? {},
      enabled: true,
      startedAt: Date.now(),
    };
    this.flush();
    const manifest = this.source.manifest();
    if (manifest && this.pending(manifest).length) {
      this.announced = true;
      this.port.notify({
        kind: 'started',
        bytes: manifest.shards.reduce(
          (sum, shard) => sum + shard.encodedBytes,
          0
        ),
      });
    }
    this.start();
  }

  dispose() {
    if (this.disposed) return;
    this.disposed = true;
    this.unsubscribe();
    if (this.settleTimer) clearTimeout(this.settleTimer);
    this.stop();
    this.flush();
  }

  /** Abandons the current run; its in-flight pulls settle unobserved. */
  private stop() {
    this.run?.abort();
    this.run = null;
    this.rerun = false;
    this.announced = false;
  }

  private manifestChanged() {
    if (!this.enabled || this.settleTimer) return;
    this.settleTimer = setTimeout(() => {
      this.settleTimer = null;
      if (this.disposed || !this.enabled) return;
      this.publish();
      this.start();
    }, MANIFEST_SETTLE_MS);
  }

  private pending(manifest: DatabaseShardManifest) {
    const record = this.record!;
    const present = new Set(manifest.shards.map(shard => shard.shardId));
    for (const shardId of Object.keys(record.pulled))
      if (!present.has(shardId)) delete record.pulled[shardId];
    return manifest.shards.filter(
      shard => record.pulled[shard.shardId] !== fingerprint(shard)
    );
  }

  private start() {
    if (this.run) {
      this.rerun = true;
      return;
    }
    const manifest = this.source.manifest();
    if (!manifest) return;
    const queue = this.pending(manifest);
    if (!queue.length) {
      this.complete();
      return;
    }
    const run = new AbortController();
    this.run = run;
    this.publish('running');
    const worker = async () => {
      for (let shard = queue.shift(); shard; shard = queue.shift()) {
        await this.port.pull(shard.serverDocId, run.signal);
        if (run.signal.aborted) return;
        this.record!.pulled[shard.shardId] = fingerprint(shard);
        this.scheduleFlush();
        this.publish('running');
      }
    };
    const settle = (error?: unknown) => {
      if (this.run !== run) return;
      this.run = null;
      this.flush();
      if (error !== undefined) {
        this.rerun = false;
        this.publish('failed', errorReason(error));
        if (this.announced) {
          this.announced = false;
          this.port.notify({ kind: 'failed', reason: errorReason(error) });
        }
      } else if (this.rerun) {
        this.rerun = false;
        this.start();
      } else this.complete();
    };
    void Promise.all(
      Array.from(
        { length: Math.min(DATABASE_OFFLINE_COPY_CONCURRENCY, queue.length) },
        worker
      )
    ).then(
      () => settle(),
      (error: unknown) => {
        run.abort();
        settle(error ?? new Error('Pull failed'));
      }
    );
  }

  private complete() {
    const record = this.record!;
    if (record.completedAt === undefined) {
      record.completedAt = Date.now();
      this.flush();
    }
    this.publish('complete');
    if (this.announced) {
      this.announced = false;
      this.port.notify({ kind: 'completed' });
    }
  }

  private scheduleFlush() {
    if (this.flushTimer) return;
    this.flushTimer = setTimeout(() => this.flush(), RECORD_FLUSH_MS);
  }

  private flush() {
    if (this.flushTimer) clearTimeout(this.flushTimer);
    this.flushTimer = null;
    if (this.record) this.port.write(this.source.databaseId, this.record);
  }

  private publish(
    status: DatabaseOfflineCopyProgress['status'] = this.enabled
      ? this._progress$.value.status
      : 'idle',
    error?: string
  ) {
    const manifest = this.source.manifest();
    const pulled = this.record?.pulled ?? {};
    let loadedRows = 0,
      totalRows = 0,
      totalBytes = 0;
    for (const shard of manifest?.shards ?? []) {
      totalRows += shard.rowCount;
      totalBytes += shard.encodedBytes;
      if (pulled[shard.shardId] === fingerprint(shard))
        loadedRows += shard.rowCount;
    }
    this._progress$.value = {
      enabled: this.enabled,
      status: this.enabled ? status : 'idle',
      initial: this.record?.completedAt === undefined,
      loadedRows,
      totalRows,
      totalBytes,
      ...(error ? { error } : {}),
    };
  }
}

type SharedCopy = { copy: DatabaseOfflineCopy; owners: number };
const sharedCopies = new WeakMap<
  DatabaseOfflineCopyPort,
  Map<string, SharedCopy>
>();

/** One copy per database, shared by every view of it (page, canvas,
 * fullscreen); the last released view stops it. */
export function acquireDatabaseOfflineCopy(
  port: DatabaseOfflineCopyPort,
  source: DatabaseOfflineCopySource
): { copy: DatabaseOfflineCopy; release: () => void } {
  let copies = sharedCopies.get(port);
  if (!copies) sharedCopies.set(port, (copies = new Map()));
  let shared = copies.get(source.databaseId);
  if (!shared) {
    shared = { copy: new DatabaseOfflineCopy(port, source), owners: 0 };
    copies.set(source.databaseId, shared);
  }
  shared.owners++;
  const entry = shared;
  let released = false;
  return {
    copy: entry.copy,
    release: () => {
      if (released) return;
      released = true;
      if (--entry.owners > 0) return;
      entry.copy.dispose();
      copies.delete(source.databaseId);
    },
  };
}
